import { CdkOption, ListboxValueChangeEvent } from '@angular/cdk/listbox';
import { ConnectedOverlayPositionChange } from '@angular/cdk/overlay';
import { computed, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';

import { SelectTrigger } from '@treetop/shared/model';

type OverlaySide = 'top' | 'bottom' | 'left' | 'right';

interface SelectComponentState<T> {
  id: string;
  placeholder: string;
  isExpanded: boolean;
  disabled: boolean;
  possibleOptions: CdkOption[];
  value: T;
  triggerWidth: number;
  side: OverlaySide;
  shouldEmitChange: boolean;
}

@Injectable()
export class SelectService<T> {
  private readonly state = signal<SelectComponentState<T>>({
    id: '',
    placeholder: '',
    isExpanded: false,
    disabled: false,
    possibleOptions: [],
    value: undefined,
    triggerWidth: 0,
    side: 'bottom',
    shouldEmitChange: false,
  });

  public readonly id = computed(() => this.state().id);
  public readonly placeholder = computed(() => this.state().placeholder);
  public readonly disabled = computed(() => this.state().disabled);
  public readonly isExpanded = computed(() => this.state().isExpanded);
  public readonly selectedOption = computed(() => this.state().possibleOptions.find((option) => option.value === this.state().value));
  public readonly possibleOptions = computed(() => this.state().possibleOptions);
  public readonly value = computed(() => this.state().value);
  public readonly triggerWidth = computed(() => this.state().triggerWidth);
  public readonly overlayState = computed(() => (this.isExpanded() ? 'open' : 'closed'));
  public readonly shouldEmitChange = computed(() => this.state().shouldEmitChange);
  public readonly side = computed(() => this.state().side);

  public readonly listBoxValueChangeEvent$ = new Subject<ListboxValueChangeEvent<T>>();

  constructor() {
    this.listBoxValueChangeEvent$.pipe(takeUntilDestroyed()).subscribe((listBoxChange) => {
      const value = listBoxChange.value[0];
      this.state.update((state) => ({
        ...state,
        value: value,
        shouldEmitChange: true,
      }));
    });
  }

  // Needed due to https://github.com/angular/angular/issues/20810
  private _selectTrigger?: SelectTrigger;
  public setSelectTrigger(trigger: SelectTrigger) {
    this._selectTrigger = trigger;
  }
  public get selectTrigger() {
    return this._selectTrigger;
  }

  /**
   * Updates the state with the provided partial state.
   *
   * @param partialState - An object containing the partial state to be merged with the current state.
   */
  public updateState(partialState: Partial<SelectComponentState<T>>) {
    this.state.update((state) => ({ ...state, ...partialState }));
  }

  /**
   * Sets the initial selected option in the state.
   *
   * @param value - The value to set as the initial selected option.
   * @remarks
   * This method updates the state with the provided value and ensures that the change event is not emitted.
   */
  public setInitialSelectedOption(value: T): void {
    this.state.update((state) => ({
      ...state,
      value: value,
      shouldEmitChange: false,
      selectedOption: {
        label: state.possibleOptions?.find((option) => option.value === value)?.getLabel(),
        value: value,
      },
    }));
  }

  /**
   * Updates the state with a new set of possible options.
   *
   * @param options - An array of CdkOption objects representing the new possible options.
   */
  public setPossibleOptions(options: CdkOption[]): void {
    this.state.update((state) => ({ ...state, possibleOptions: options }));
  }

  /**
   * Sets the expanded state of the component.
   *
   * @param isExpanded - A boolean indicating whether the component should be expanded.
   */
  public setExpanded(isExpanded: boolean): void {
    this.state.update((state) => ({ ...state, isExpanded }));
  }

  /**
   * Updates the side based on the given ConnectedOverlayPositionChange.
   *
   * @param {ConnectedOverlayPositionChange} change - The change object containing the new connection pair values.
   */
  public setSide(change: ConnectedOverlayPositionChange): void {
    const { originX, originY } = change.connectionPair;

    let side: OverlaySide;
    if (originY === 'center') {
      side = originX === 'start' ? 'left' : 'right';
    } else {
      side = originY;
    }

    this.state.update((state) => ({ ...state, side }));
  }
}
